import { useState, useEffect, useRef } from "react";
import { api, formatCurrency, formatDate, statusLabels, statusColors, logisticsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { PvDialog } from "@/components/PvDialog";
import { WorkSessionsDialog } from "@/components/WorkSessionsDialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Filter,
  Download,
  CalendarIcon,
  Loader2,
  FileText,
  Ban,
  Pause,
  Play,
  FileSignature,
  HardHat,
  CreditCard,
  History,
  Package,
  Eye,
  EyeOff,
  Mail,
  Upload,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Columns3,
  AlertTriangle,
  ListChecks,
  X
} from "lucide-react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { useTableColumns } from "@/hooks/useTableColumns";
import { ColumnsToggle, SortableTh } from "@/components/TableControls";
import OrdersTable from "@/components/orders/OrdersTable";
import MultiSelectFilter from "@/components/MultiSelectFilter";
import CompatibleProductsDialog from "@/components/suppliers/CompatibleProductsDialog";
import BulkOrderDateDialog from "@/components/orders/dialogs/BulkOrderDateDialog";
import DeleteOrderAlertDialog from "@/components/orders/dialogs/DeleteOrderAlertDialog";
import OnHoldDialog from "@/components/orders/dialogs/OnHoldDialog";
import SendReminderDialog from "@/components/orders/dialogs/SendReminderDialog";
import WorkforceWarningDialog from "@/components/orders/dialogs/WorkforceWarningDialog";
import OrderProgressDialog from "@/components/orders/OrderProgressDialog";
import OrderDuplicatesDialog from "@/components/orders/OrderDuplicatesDialog";
import OrderFormDialog from "@/components/orders/OrderFormDialog";
import BulkEditOrdersDialog from "@/components/orders/dialogs/BulkEditOrdersDialog";
import PaymentsDialog from "@/components/orders/dialogs/PaymentsDialog";
import GlobalLogisticsTodoDialog from "@/components/orders/GlobalLogisticsTodoDialog";
import TimeDeltaBadge from "@/components/TimeDeltaBadge";
import ProfitabilityBadge from "@/components/ProfitabilityBadge";

export const OrdersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const PAGE_SIZE = 50;
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  // Si /api/orders-paginated indisponible (404), on bascule en mode fallback :
  // on récupère toutes les commandes en une seule fois et on pagine côté client.
  const [allOrdersCache, setAllOrdersCache] = useState(null);
  const [materialMargins, setMaterialMargins] = useState({});
  const [timeDeltas, setTimeDeltas] = useState({});
  const [profitability, setProfitability] = useState({});
  const [orderProgressCumulatives, setOrderProgressCumulatives] = useState({});
  const [orderTypes, setOrderTypes] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [workforceWarning, setWorkforceWarning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const ORDER_FILTERS_STORAGE_KEY = "orders-filters-v1";
  const defaultFilters = {
    status: [],
    seller_id: [],
    payment: [],
    profit: [],
    progress: [],
    worker: [],
    order_date_from: null,
    order_date_to: null,
    missing_order_date: false,
    money_out: false,
  };
  // Restore filters from localStorage on first mount (synchronous pour éviter
  // une fetch initiale qui serait ensuite remplacée)
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState(() => {
    try {
      const raw = localStorage.getItem(ORDER_FILTERS_STORAGE_KEY);
      if (!raw) return defaultFilters;
      const parsed = JSON.parse(raw);
      return {
        ...defaultFilters,
        ...parsed,
        // Les dates doivent redevenir des Date après JSON.parse
        order_date_from: parsed.order_date_from ? new Date(parsed.order_date_from) : null,
        order_date_to: parsed.order_date_to ? new Date(parsed.order_date_to) : null,
      };
    } catch {
      return defaultFilters;
    }
  });
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [bulkDateDialogOpen, setBulkDateDialogOpen] = useState(false);
  const [bulkDate, setBulkDate] = useState(null);
  
  // On hold dialog
  const [holdDialogOpen, setHoldDialogOpen] = useState(false);
  const [orderToHold, setOrderToHold] = useState(null);
  const [holdData, setHoldData] = useState({
    is_on_hold: true,
    on_hold_reason: "",
    exclude_from_planning: true,
    exclude_from_stock: true
  });
  
  // Payments dialog
  const [paymentsDialogOpen, setPaymentsDialogOpen] = useState(false);
  const [selectedOrderForPayments, setSelectedOrderForPayments] = useState(null);
  const [compatibleProductsOrder, setCompatibleProductsOrder] = useState(null);
  const [pvOrder, setPvOrder] = useState(null);
  const [workSessionsOrder, setWorkSessionsOrder] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [reminderDialog, setReminderDialog] = useState({ open: false, order: null, email: "", message: "", sending: false });
  const [progressDialogOrder, setProgressDialogOrder] = useState(null);
  const [duplicatesDialogOpen, setDuplicatesDialogOpen] = useState(false);
  const [bulkEditOrdersOpen, setBulkEditOrdersOpen] = useState(false);

  // Colonnes configurables (tri + masquage)
  const ORDER_COLUMNS = [
    { key: "reference", label: "Référence" },
    { key: "order_date", label: "Date commande" },
    { key: "work_description", label: "Travaux" },
    { key: "client_name", label: "Client" },
    { key: "city", label: "Ville" },
    { key: "amount_ht", label: "Montant HT" },
    { key: "vat_rate", label: "TVA" },
    { key: "amount_ttc", label: "Montant TTC" },
    { key: "order_type_name", label: "Type" },
    { key: "seller_name", label: "Vendeur" },
    { key: "estimated_duration", label: "Durée est." },
    { key: "status", label: "Statut" },
    { key: "progress", label: "Avancement", sortable: false },
    { key: "payment", label: "Paiements", sortable: false },
    { key: "actions", label: "Actions", sortable: false, always: true },
  ];
  const {
    visibleColumns: visibleOrderColumns,
    orderedColumns: orderedOrderColumns,
    isVisible: isOrderColVisible,
    toggleVisibility: toggleOrderCol,
    handleSort: handleOrderSort,
    sortArrow: orderSortArrow,
    sortItems: sortOrders,
    moveColumn: moveOrderColumn,
    resetOrder: resetOrderColumns,
  } = useTableColumns({
    storageKey: "orders-table",
    columns: ORDER_COLUMNS,
    initialSort: { key: "order_date", direction: "desc" },
  });

  // Reload material margins after closing the dialog (user may have locked/removed materials)
  const handleCloseCompatible = () => {
    setCompatibleProductsOrder(null);
    fetchData();
  };
  const [orderPayments, setOrderPayments] = useState({ payments: [], invoices: [], total_paid: 0, remaining: 0, total_invoiced_ttc: 0, total_invoiced_ht: 0 });
  const [logisticsSummary, setLogisticsSummary] = useState({});
  const [globalLogisticsOpen, setGlobalLogisticsOpen] = useState(false);
  useEffect(() => {
    logisticsApi.getOrdersLogisticsSummary()
      .then(({ data }) => setLogisticsSummary(data || {}))
      .catch(() => {});
  }, []);
  const [newPayment, setNewPayment] = useState({
    amount: "",
    payment_date: new Date(),
    payment_type: "intermediaire",
    notes: ""
  });
  
  const [formData, setFormData] = useState({
    reference: "",
    client_name: "",
    client_email: "",
    city: "",
    amount_ht: "",
    vat_rate: 20,
    order_type_id: "",
    seller_id: "",
    executants: 1,
    notes: "",
    status: "pending",
    deposit_paid: false,
    deposit_date: null,
    deposit_amount: "",
    order_paid: false,
    paid_date: null,
    allow_saturday: false,
    allow_sunday: false,
    custom_duration: "",
    subcontractor_id: "",
    subcontractor_contract_amount: "",
    target_margin_percent: "",
    material_cost_override: "",
    order_date: null,
    work_description: "",
    // Phase 179
    site_address_line1: "",
    site_address_line2: "",
    site_postal_code: "",
    site_city: "",
    site_country: "",
    client_interlocutor_index: null,
    client_interlocutor_name: null,
    client_interlocutor_role: null,
    client_interlocutor_phone: null,
    client_interlocutor_email: null,
  });

  // Compteur de requête : seul le résultat de la dernière requête lancée est appliqué.
  // Évite la course entre 2 fetchData concurrentes (ex: mount + debounce search) où une
  // réponse "tout" arrive APRÈS une réponse "filtrée" et écrase la liste affichée.
  const fetchSeqRef = useRef(0);

  const fetchData = async () => {
    const myId = ++fetchSeqRef.current;
    try {
      setLoading(true);
      const apiFilters = {
        ...(filters.order_date_from && { order_date_from: format(filters.order_date_from, "yyyy-MM-dd") }),
        ...(filters.order_date_to && { order_date_to: format(filters.order_date_to, "yyyy-MM-dd") }),
        ...(filters.missing_order_date && { missing_order_date: true }),
        ...(searchQuery.trim() && { search: searchQuery.trim() }),
        ...(showHidden && { include_hidden: true }),
      };
      // Tentative paginée. Fallback gracieux vers /api/orders si l'endpoint
      // n'existe pas (ex: backend non redéployé) ou s'il erreure.
      const ordersPromise = api
        .getOrdersPaginated({ ...apiFilters, limit: PAGE_SIZE, skip: 0 })
        .then((r) => ({
          items: r.data.items || [],
          total: r.data.total ?? (r.data.items || []).length,
          has_more: !!r.data.has_more,
          fallback: false,
        }))
        .catch(async () => {
          // Fallback legacy : récupère TOUT et fait la pagination côté client.
          const r = await api.getOrders(apiFilters);
          const all = Array.isArray(r.data) ? r.data : [];
          return {
            items: all.slice(0, PAGE_SIZE),
            total: all.length,
            has_more: all.length > PAGE_SIZE,
            fallback: true,
            allOrdersCache: all, // garde tout en mémoire pour loadMore
          };
        });

      const [ordersResult, typesRes, sellersRes, subsRes, empRes, marginsRes, deltasRes, profitRes, cumRes] = await Promise.all([
        ordersPromise,
        api.getOrderTypes().catch(() => ({ data: [] })),
        api.getSellers().catch(() => ({ data: [] })),
        api.getSubcontractors().catch(() => ({ data: [] })),
        api.getEmployees().catch(() => ({ data: [] })),
        api.getMaterialMargins().catch(() => ({ data: { orders: [] } })),
        api.getOrdersTimeDeltas().catch(() => ({ data: {} })),
        api.getOrdersProfitability().catch(() => ({ data: {} })),
        api.getOrderProgressCumulatives().catch(() => ({ data: {} })),
      ]);
      if (myId !== fetchSeqRef.current) return;
      setOrders(ordersResult.items);
      setTotalCount(ordersResult.total);
      setHasMore(ordersResult.has_more);
      setAllOrdersCache(ordersResult.allOrdersCache || null);
      setOrderTypes(typesRes.data);
      setSellers(sellersRes.data);
      setSubcontractors(subsRes.data);
      setEmployees(empRes.data);
      const marginsByOrder = {};
      for (const m of (marginsRes.data.orders || [])) marginsByOrder[m.order_id] = m;
      setMaterialMargins(marginsByOrder);
      setTimeDeltas(deltasRes.data || {});
      setProfitability(profitRes.data || {});
      setOrderProgressCumulatives(cumRes.data || {});
    } catch (error) {
      if (myId !== fetchSeqRef.current) return;
      toast.error("Erreur lors du chargement");
      console.error("[OrdersPage] fetchData failed:", error?.response?.status, error?.response?.data, error);
    } finally {
      if (myId === fetchSeqRef.current) setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      // Mode fallback : on a déjà toutes les commandes en mémoire, on slice
      if (allOrdersCache) {
        const next = allOrdersCache.slice(orders.length, orders.length + PAGE_SIZE);
        setOrders((prev) => [...prev, ...next]);
        setHasMore(orders.length + next.length < allOrdersCache.length);
        return;
      }
      const apiFilters = {
        ...(filters.order_date_from && { order_date_from: format(filters.order_date_from, "yyyy-MM-dd") }),
        ...(filters.order_date_to && { order_date_to: format(filters.order_date_to, "yyyy-MM-dd") }),
        ...(filters.missing_order_date && { missing_order_date: true }),
        ...(searchQuery.trim() && { search: searchQuery.trim() }),
        ...(showHidden && { include_hidden: true }),
      };
      const res = await api.getOrdersPaginated({ ...apiFilters, limit: PAGE_SIZE, skip: orders.length });
      const newItems = res.data.items || [];
      setOrders((prev) => [...prev, ...newItems]);
      setHasMore(!!res.data.has_more);
      setTotalCount(res.data.total || 0);
    } catch (e) {
      toast.error("Erreur lors du chargement supplémentaire");
      console.error("[OrdersPage] loadMore failed:", e);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, showHidden]);

  // Recherche serveur avec debounce 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Persister les filtres (hors "missing_order_date" qui vient d'une URL ponctuelle)
  useEffect(() => {
    try {
      const { missing_order_date, ...persisted } = filters;
      localStorage.setItem(ORDER_FILTERS_STORAGE_KEY, JSON.stringify(persisted));
    } catch {
      /* ignore quota errors */
    }
  }, [filters]);

  // Activer le filtre "sans date de commande" si présent dans l'URL (depuis la bannière dashboard)
  useEffect(() => {
    if (searchParams.get("missing_order_date") === "true") {
      setFilters((f) => ({ ...f, missing_order_date: true }));
    }
  }, [searchParams]);

  // Activer le filtre "Argent dehors" depuis la tuile Dashboard : ?filter=money_out
  useEffect(() => {
    if (searchParams.get("filter") === "money_out") {
      setFilters((f) => ({ ...f, money_out: true }));
      const np = new URLSearchParams(searchParams);
      np.delete("filter");
      setSearchParams(np, { replace: true });
      toast.success("Filtre « Argent dehors » appliqué");
    }
  }, [searchParams, setSearchParams]);

  // Filtrer par statut depuis l'URL : ?status=pending,in_progress (depuis tuile "Stock en attente")
  useEffect(() => {
    const statusParam = searchParams.get("status");
    if (statusParam) {
      const statuses = statusParam.split(",").map((s) => s.trim()).filter(Boolean);
      if (statuses.length) {
        setFilters((f) => ({ ...f, status: statuses }));
        // On retire le param après application pour ne pas re-déclencher
        const np = new URLSearchParams(searchParams);
        np.delete("status");
        setSearchParams(np, { replace: true });
        toast.success(`Filtré sur ${statuses.length} statut(s) depuis le tableau de bord`);
      }
    }
  }, [searchParams, setSearchParams]);

  // Filtre transient : commandes avec un avancement saisi sur un mois précis
  // ?progress_year=2026&progress_month=4 (depuis tuile "CA réalisé du mois")
  const [progressMonthFilter, setProgressMonthFilter] = useState(null); // {year, month}
  const [progressOrderIds, setProgressOrderIds] = useState(null); // null = filtre inactif, [] = aucune commande
  useEffect(() => {
    const py = searchParams.get("progress_year");
    const pm = searchParams.get("progress_month");
    if (py && pm) {
      setProgressMonthFilter({ year: parseInt(py, 10), month: parseInt(pm, 10) });
      const np = new URLSearchParams(searchParams);
      np.delete("progress_year");
      np.delete("progress_month");
      setSearchParams(np, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const load = async () => {
      if (!progressMonthFilter) {
        setProgressOrderIds(null);
        return;
      }
      try {
        const { data } = await api.getOrderProgress({
          year: progressMonthFilter.year,
          month: progressMonthFilter.month,
        });
        const ids = Array.from(new Set((data || []).map((p) => p.order_id)));
        setProgressOrderIds(ids);
        toast.success(`${ids.length} commande(s) avec un avancement en ${progressMonthFilter.month}/${progressMonthFilter.year}`);
      } catch {
        toast.error("Erreur chargement avancements du mois");
        setProgressOrderIds([]);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressMonthFilter]);

  // Focus sur une commande précise depuis un lien externe (fiche client, dashboard, etc.)
  // Quand ?focus={orderId} est présent, on :
  //  1. Attend que les commandes soient chargées
  //  2. Trouve la commande (active hidden si besoin)
  //  3. Scroll sur la ligne + highlight 3s
  //  4. Ouvre le dialog d'édition (vue détaillée)
  //  5. Nettoie le param URL pour éviter les re-triggers
  const [focusOrderId, setFocusOrderId] = useState(null);
  useEffect(() => {
    const f = searchParams.get("focus");
    if (f) setFocusOrderId(f);
  }, [searchParams]);

  useEffect(() => {
    if (!focusOrderId || !orders?.length) return;
    const match = orders.find((o) => o.id === focusOrderId);
    if (!match) {
      // Pas trouvé — probablement une commande masquée : on active le flag et on réessaie au prochain fetch
      if (!showHidden) {
        setShowHidden(true);
      }
      return;
    }
    // Scroll + highlight
    setTimeout(() => {
      const row = document.querySelector(`[data-testid="order-row-${focusOrderId}"]`);
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        row.classList.add("ring-2", "ring-primary", "ring-offset-2", "transition-all");
        setTimeout(() => row.classList.remove("ring-2", "ring-primary", "ring-offset-2"), 3000);
      }
    }, 100);
    // Ouvre le dialog d'édition (vue complète)
    openDialog(match);
    // Nettoie le param URL
    const np = new URLSearchParams(searchParams);
    np.delete("focus");
    setSearchParams(np, { replace: true });
    setFocusOrderId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusOrderId, orders]);

  const resetForm = () => {
    setFormData({
      reference: "",
      client_name: "",
    client_email: "",
      city: "",
      amount_ht: "",
      vat_rate: 20,
      order_type_id: "",
      seller_id: "",
      executants: 1,
      notes: "",
      status: "pending",
      deposit_paid: false,
      deposit_date: null,
      deposit_amount: "",
      order_paid: false,
      paid_date: null,
      allow_saturday: false,
      allow_sunday: false,
      custom_duration: "",
      subcontractor_id: "",
      subcontractor_contract_amount: "",
      target_margin_percent: "",
      material_cost_override: "",
      order_date: null,
      work_description: "",
      site_address_line1: "",
      site_address_line2: "",
      site_postal_code: "",
      site_city: "",
      site_country: "",
      client_interlocutor_index: null,
      client_interlocutor_name: null,
      client_interlocutor_role: null,
      client_interlocutor_phone: null,
      client_interlocutor_email: null,
    });
    setEditingOrder(null);
  };

  const openDialog = (order = null) => {
    if (order) {
      setEditingOrder(order);
      setFormData({
        reference: order.reference || "",
        client_name: order.client_name,
        client_email: order.client_email || "",
        city: order.city || "",
        amount_ht: order.amount_ht,
        vat_rate: order.vat_rate ?? 20,
        order_type_id: order.order_type_id,
        seller_id: order.seller_id || "",
        executants: order.executants,
        notes: order.notes || "",
        status: order.status,
        deposit_paid: order.deposit_paid,
        deposit_date: order.deposit_date ? new Date(order.deposit_date) : null,
        deposit_amount: order.deposit_amount || "",
        order_paid: order.order_paid,
        paid_date: order.paid_date ? new Date(order.paid_date) : null,
        allow_saturday: order.allow_saturday || false,
        allow_sunday: order.allow_sunday || false,
        custom_duration: order.custom_duration || "",
        subcontractor_id: order.subcontractor_id || "",
        subcontractor_contract_amount: order.subcontractor_contract_amount || "",
        target_margin_percent: order.target_margin_percent ?? "",
        material_cost_override: order.material_cost_override ?? "",
        order_date: order.order_date ? new Date(order.order_date) : null,
        work_description: order.work_description ?? "",
        site_address_line1: order.site_address_line1 || "",
        site_address_line2: order.site_address_line2 || "",
        site_postal_code: order.site_postal_code || "",
        site_city: order.site_city || "",
        site_country: order.site_country || "",
        client_interlocutor_index: (order.client_interlocutor_index !== undefined ? order.client_interlocutor_index : null),
        client_interlocutor_name: order.client_interlocutor_name || null,
        client_interlocutor_role: order.client_interlocutor_role || null,
        client_interlocutor_phone: order.client_interlocutor_phone || null,
        client_interlocutor_email: order.client_interlocutor_email || null,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const openDialogWithClient = (client) => {
    resetForm();
    setEditingOrder(null);
    // Mise à jour post-reset pour pré-remplir les infos client
    setTimeout(() => {
      setFormData((prev) => ({
        ...prev,
        client_name: client.client_name || "",
        client_email: client.client_email || "",
        city: client.city || "",
      }));
      setDialogOpen(true);
    }, 0);
  };

  // Support d'un state { prefillClient, openCreateDialog } passé par navigate()
  // depuis /clients (création ou détail) pour rattacher une commande au client choisi.
  useEffect(() => {
    const s = location?.state;
    if (!s || !s.openCreateDialog || !s.prefillClient) return;
    openDialogWithClient(s.prefillClient);
    // Nettoyer le state pour éviter une ré-ouverture sur next mount
    window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.key]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const hasSubcontractor = !!formData.subcontractor_id;
      const data = {
        ...formData,
        reference: formData.reference ? formData.reference.trim() : null,
        amount_ht: parseFloat(formData.amount_ht),
        vat_rate: parseFloat(formData.vat_rate) || 20,
        executants: parseInt(formData.executants),
        deposit_date: formData.deposit_date ? formData.deposit_date.toISOString() : null,
        deposit_amount: formData.deposit_amount ? parseFloat(formData.deposit_amount) : null,
        paid_date: formData.paid_date ? formData.paid_date.toISOString() : null,
        seller_id: formData.seller_id || null,
        allow_saturday: formData.allow_saturday,
        allow_sunday: formData.allow_sunday,
        custom_duration: formData.custom_duration ? parseFloat(formData.custom_duration) : null,
        subcontractor_id: formData.subcontractor_id || null,
        subcontractor_contract_amount: formData.subcontractor_contract_amount ? parseFloat(formData.subcontractor_contract_amount) : null,
        target_margin_percent: formData.target_margin_percent === "" || formData.target_margin_percent === null
          ? null
          : parseFloat(formData.target_margin_percent),
        material_cost_override: formData.material_cost_override === "" || formData.material_cost_override === null
          ? null
          : parseFloat(formData.material_cost_override),
        order_date: formData.order_date
          ? (formData.order_date instanceof Date
              ? format(formData.order_date, "yyyy-MM-dd")
              : String(formData.order_date).slice(0, 10))
          : null,
        work_description: (formData.work_description || "").trim() || null,
        // Phase 179 — site address + interlocutor (cleanup empty strings)
        site_address_line1: (formData.site_address_line1 || "").trim() || null,
        site_address_line2: (formData.site_address_line2 || "").trim() || null,
        site_postal_code: (formData.site_postal_code || "").trim() || null,
        site_city: (formData.site_city || "").trim() || null,
        site_country: (formData.site_country || "").trim() || null,
        client_interlocutor_index: formData.client_interlocutor_index === undefined ? null : formData.client_interlocutor_index,
        client_interlocutor_name: (formData.client_interlocutor_name || "").trim() || null,
        client_interlocutor_role: (formData.client_interlocutor_role || "").trim() || null,
        client_interlocutor_phone: (formData.client_interlocutor_phone || "").trim() || null,
        client_interlocutor_email: (formData.client_interlocutor_email || "").trim() || null,
      };

      // Check workforce availability only if NOT assigned to subcontractor
      if (!hasSubcontractor && !editingOrder) {
        try {
          const today = new Date().toISOString().split('T')[0];
          const endCheck = new Date();
          endCheck.setDate(endCheck.getDate() + 180);
          const check = await api.workforceCheck({
            start_date: today,
            end_date: endCheck.toISOString().split('T')[0],
            executants_needed: data.executants
          });
          if (check.data.has_conflict) {
            setWorkforceWarning({
              data: check.data,
              orderData: data
            });
            return; // Attendre confirmation utilisateur
          }
        } catch (err) {
          console.warn("Workforce check failed", err);
        }
      }

      if (editingOrder) {
        await api.updateOrder(editingOrder.id, data);
        toast.success("Commande mise à jour");
      } else {
        await api.createOrder(data);
        toast.success("Commande créée");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const confirmCreateDespiteWarning = async () => {
    if (!workforceWarning) return;
    try {
      await api.createOrder(workforceWarning.orderData);
      toast.success("Commande créée");
      setWorkforceWarning(null);
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (id) => {
    setOrderToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!orderToDelete) return;
    const id = orderToDelete;
    const snapshot = orders.find((o) => o.id === id);
    setDeleteDialogOpen(false);
    setOrderToDelete(null);

    try {
      const response = await api.deleteOrder(id);
      if (!response.data.success) {
        toast.error("Erreur lors de la suppression");
        return;
      }
      // Optimistic remove from list
      setOrders((prev) => prev.filter((o) => o.id !== id));

      let undone = false;
      toast.success(`Commande ${snapshot?.reference || ""} supprimée`, {
        duration: 5000,
        action: {
          label: "Annuler",
          onClick: async () => {
            undone = true;
            try {
              await api.restoreOrder(id);
              toast.success("Commande restaurée");
              fetchData();
            } catch {
              toast.error("Impossible de restaurer la commande");
            }
          },
        },
      });

      // Hard delete après le délai si pas d'annulation
      setTimeout(async () => {
        if (undone) return;
        try {
          await api.hardDeleteOrder(id);
        } catch {
          // silent: le soft delete reste actif côté serveur
        }
      }, 5500);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleCancelOrder = async (id) => {
    try {
      await api.updateOrder(id, { status: "cancelled" });
      toast.success("Commande annulée");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'annulation");
    }
  };

  // On Hold Functions
  const openHoldDialog = (order) => {
    setOrderToHold(order);
    setHoldData({
      is_on_hold: !order.is_on_hold,
      on_hold_reason: order.on_hold_reason || "",
      exclude_from_planning: order.exclude_from_planning ?? true,
      exclude_from_stock: order.exclude_from_stock ?? true
    });
    setHoldDialogOpen(true);
  };

  const handleToggleHold = async () => {
    if (!orderToHold) return;
    try {
      await api.toggleOrderHold(orderToHold.id, holdData);
      toast.success(holdData.is_on_hold ? "Commande mise en pause" : "Commande réactivée");
      setHoldDialogOpen(false);
      setOrderToHold(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  // Payments Functions
  const openPaymentsDialog = async (order) => {
    setSelectedOrderForPayments(order);
    setPaymentsDialogOpen(true);
    try {
      const res = await api.getOrderPayments(order.id);
      setOrderPayments(res.data);
    } catch (error) {
      console.error("Error fetching payments:", error);
      setOrderPayments({
        payments: [],
        total_paid: 0,
        remaining: order.amount_ttc ?? (order.amount_ht || 0) * (1 + (order.vat_rate ?? 20) / 100),
      });
    }
  };

  const handleAddPayment = async () => {
    if (!selectedOrderForPayments || !newPayment.amount) return;
    try {
      await api.addOrderPayment(selectedOrderForPayments.id, {
        ...newPayment,
        order_id: selectedOrderForPayments.id,
        amount: parseFloat(newPayment.amount),
        payment_date: format(newPayment.payment_date, "yyyy-MM-dd")
      });
      toast.success("Paiement ajouté");
      
      // Refresh payments
      const res = await api.getOrderPayments(selectedOrderForPayments.id);
      setOrderPayments(res.data);
      
      // Reset form
      setNewPayment({
        amount: "",
        payment_date: new Date(),
        payment_type: "intermediaire",
        notes: ""
      });
      
      fetchData(); // Refresh orders to update total_paid
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!selectedOrderForPayments) return;
    try {
      await api.deleteOrderPayment(selectedOrderForPayments.id, paymentId);
      toast.success("Paiement supprimé");
      
      // Refresh payments
      const res = await api.getOrderPayments(selectedOrderForPayments.id);
      setOrderPayments(res.data);
      
      fetchData(); // Refresh orders
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const handleToggleDeposit = async (orderId) => {
    try {
      await api.toggleDepositPaid(orderId);
      toast.success("Statut acompte mis à jour");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleToggleHidden = async (order) => {
    try {
      await api.updateOrder(order.id, { hidden: !order.hidden });
      toast.success(order.hidden ? "Commande réaffichée" : "Commande masquée");
      fetchData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleSendReminder = async () => {
    if (!reminderDialog.email || !reminderDialog.email.includes("@")) {
      toast.error("Email client requis");
      return;
    }
    try {
      setReminderDialog(d => ({ ...d, sending: true }));
      await api.sendDepositReminder(reminderDialog.order.id, {
        client_email: reminderDialog.email,
        custom_message: reminderDialog.message || null,
      });
      toast.success(`Email de relance envoyé à ${reminderDialog.email}`);
      setReminderDialog({ open: false, order: null, email: "", message: "", sending: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
      setReminderDialog(d => ({ ...d, sending: false }));
    }
  };

  const handleTogglePaid = async (orderId) => {
    try {
      await api.toggleOrderPaid(orderId);
      toast.success("Statut solde mis à jour");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleMarkCashed = async (order) => {
    const confirmed = window.confirm(
      `Encaisser la commande "${order.reference}" ?\n\nElle passera au statut "Encaissée" et la commission du vendeur sera prise en compte.`
    );
    if (!confirmed) return;
    try {
      await api.updateOrder(order.id, { status: "cashed" });
      toast.success("Commande encaissée — commission vendeur déclenchée");
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors du passage à 'Encaissée'");
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.exportOrders(filters);
      const data = response.data.data;
      
      // Convert to CSV
      const headers = Object.keys(data[0] || {}).join(";");
      const rows = data.map(row => Object.values(row).join(";")).join("\n");
      const csv = `${headers}\n${rows}`;
      
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `commandes_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      
      toast.success("Export réussi");
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleBackfillOrderDates = async () => {
    const ok = window.confirm(
      "Remplir automatiquement la date de commande manquante pour toutes les commandes ?\n\n" +
        "La date utilisée sera : la date d'acompte si elle existe, sinon la date du solde.\n" +
        "Les commandes ayant déjà une date de commande ne seront pas modifiées."
    );
    if (!ok) return;
    try {
      const res = await api.backfillOrderDates();
      const d = res.data || {};
      toast.success(
        `Backfill terminé : ${d.updated || 0} commande(s) mise(s) à jour, ` +
          `${d.skipped_already_set || 0} ignorée(s) (déjà datée), ` +
          `${d.skipped_no_candidate || 0} sans acompte ni solde.`
      );
      fetchData();
    } catch {
      toast.error("Erreur lors du remplissage automatique");
    }
  };

  const handleBulkSetDate = async () => {
    if (!bulkDate || selectedOrderIds.length === 0) return;
    try {
      const res = await api.bulkSetOrderDate({
        order_ids: selectedOrderIds,
        order_date: format(bulkDate, "yyyy-MM-dd"),
      });
      toast.success(
        `${res.data?.updated || 0} commande(s) mise(s) à jour avec la date ${format(bulkDate, "dd/MM/yyyy", { locale: fr })}`
      );
      setBulkDateDialogOpen(false);
      setBulkDate(null);
      setSelectedOrderIds([]);
      fetchData();
    } catch {
      toast.error("Erreur lors de la mise à jour en masse");
    }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Commandes</h1>
          <p className="text-muted-foreground mt-1">
            Gérez vos commandes clients
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <Button variant="outline" onClick={handleExport} data-testid="export-orders-btn">
            <Download className="h-4 w-4 mr-2" />
            Exporter
          </Button>
          <Button variant="outline" onClick={() => navigate("/import-commandes")} data-testid="import-orders-btn">
            <Upload className="h-4 w-4 mr-2" />
            Importer Excel
          </Button>
          <Button
            variant="outline"
            onClick={handleBackfillOrderDates}
            data-testid="backfill-order-dates-btn"
            title="Remplit automatiquement la date de commande manquante avec la date d'acompte (ou de solde à défaut)"
          >
            <CalendarIcon className="h-4 w-4 mr-2" />
            Remplir dates manquantes
          </Button>
          <Button
            variant="outline"
            onClick={() => setDuplicatesDialogOpen(true)}
            data-testid="detect-order-duplicates-btn"
            title="Détecte les commandes en double (même client, ville, montant ±5%)"
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            Doublons
          </Button>
          <Button
            variant="outline"
            onClick={() => setGlobalLogisticsOpen(true)}
            data-testid="open-global-logistics-btn"
            title="Voir tous les besoins logistiques (cross-commandes)"
          >
            <ListChecks className="h-4 w-4 mr-2" />
            À traiter et commander
          </Button>
          <Button onClick={() => openDialog()} data-testid="add-order-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle commande
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              placeholder="Rechercher (client, ville, CP, référence, travaux…)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="orders-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="orders-search-clear"
                aria-label="Effacer la recherche"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-4">
            <MultiSelectFilter
              label="Tous les statuts"
              testId="filter-status"
              selected={filters.status}
              onChange={(next) => setFilters({ ...filters, status: next })}
              options={[
                { value: "pending", label: "En attente" },
                { value: "planning", label: "Au planning" },
                { value: "in_progress", label: "En cours" },
                { value: "ready", label: "Prête" },
                { value: "paused", label: "En pause" },
                { value: "completed", label: "Terminée" },
                { value: "cashed", label: "Encaissée" },
                { value: "cancelled", label: "Annulée" },
              ]}
            />
            <MultiSelectFilter
              label="Tous les vendeurs"
              testId="filter-seller"
              selected={filters.seller_id}
              onChange={(next) => setFilters({ ...filters, seller_id: next })}
              options={sellers.map((s) => ({ value: s.id, label: s.name }))}
            />
            <MultiSelectFilter
              label="Tous les paiements"
              testId="filter-payment"
              selected={filters.payment}
              onChange={(next) => setFilters({ ...filters, payment: next })}
              options={[
                { value: "unpaid", label: "Non payées (0%)" },
                { value: "partial", label: "Partielles (1-99%)" },
                { value: "paid", label: "Soldées (100%)" },
                { value: "overdue", label: "À relancer (+30j non soldées)" },
              ]}
            />
            <MultiSelectFilter
              label="Toute rentabilité"
              testId="filter-profit"
              selected={filters.profit}
              onChange={(next) => setFilters({ ...filters, profit: next })}
              options={[
                { value: "profitable", label: "Rentables (≥ cible)" },
                { value: "below", label: "Sous la cible" },
                { value: "negative", label: "Déficitaires" },
                { value: "no_data", label: "Sans données" },
              ]}
            />
            <MultiSelectFilter
              label="Tout avancement"
              testId="filter-progress"
              selected={filters.progress}
              onChange={(next) => setFilters({ ...filters, progress: next })}
              options={[
                { value: "empty", label: "0% (aucun)" },
                { value: "partial", label: "Partiel (< 50%)" },
                { value: "advanced", label: "Avancé (≥ 50%)" },
                { value: "complete", label: "Complet (100%)" },
                { value: "over", label: "Avenant (> 100%)" },
              ]}
            />
            <MultiSelectFilter
              label="Tous les intervenants"
              testId="filter-worker"
              widthClass="min-w-[220px]"
              selected={filters.worker}
              onChange={(next) => setFilters({ ...filters, worker: next })}
              options={[
                ...employees.map((e) => ({
                  value: `emp-${e.id}`,
                  label: `${e.first_name} ${e.last_name}`.trim(),
                  group: "Salariés",
                })),
                ...subcontractors.map((s) => ({
                  value: `sub-${s.id}`,
                  label: s.company_name || `${s.first_name || ""} ${s.last_name || ""}`.trim(),
                  group: "Sous-traitants",
                })),
              ]}
            />
            <div className="flex items-center gap-2 min-w-fit">
              <input
                type="checkbox"
                id="show-hidden-orders"
                className="h-4 w-4"
                checked={showHidden}
                onChange={(e) => setShowHidden(e.target.checked)}
                data-testid="toggle-show-hidden"
              />
              <Label htmlFor="show-hidden-orders" className="cursor-pointer text-sm whitespace-nowrap">
                Afficher masquées
              </Label>
            </div>
            <div className="flex items-center gap-2 min-w-fit" data-testid="filter-order-date-range">
              <Label className="text-sm whitespace-nowrap text-muted-foreground">Date cmd</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9"
                    data-testid="filter-order-date-from"
                  >
                    <CalendarIcon className="h-3.5 w-3.5 mr-1" />
                    {filters.order_date_from
                      ? format(filters.order_date_from, "dd/MM/yyyy", { locale: fr })
                      : "Du"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.order_date_from}
                    onSelect={(d) => setFilters({ ...filters, order_date_from: d || null })}
                    locale={fr}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <span className="text-muted-foreground text-xs">→</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9"
                    data-testid="filter-order-date-to"
                  >
                    <CalendarIcon className="h-3.5 w-3.5 mr-1" />
                    {filters.order_date_to
                      ? format(filters.order_date_to, "dd/MM/yyyy", { locale: fr })
                      : "Au"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.order_date_to}
                    onSelect={(d) => setFilters({ ...filters, order_date_to: d || null })}
                    locale={fr}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {(filters.order_date_from || filters.order_date_to) && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 px-2 text-xs"
                  onClick={() => setFilters({ ...filters, order_date_from: null, order_date_to: null })}
                  data-testid="filter-order-date-clear"
                >
                  Effacer
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 min-w-fit">
              <input
                type="checkbox"
                id="filter-missing-order-date"
                className="h-4 w-4"
                checked={filters.missing_order_date}
                onChange={(e) => {
                  setFilters({ ...filters, missing_order_date: e.target.checked });
                  if (!e.target.checked) {
                    // Nettoyer le param URL s'il était là
                    if (searchParams.get("missing_order_date")) {
                      const np = new URLSearchParams(searchParams);
                      np.delete("missing_order_date");
                      setSearchParams(np, { replace: true });
                    }
                  }
                }}
                data-testid="filter-missing-order-date"
              />
              <Label htmlFor="filter-missing-order-date" className="cursor-pointer text-sm whitespace-nowrap">
                Sans date de commande
              </Label>
            </div>
            <div className="flex items-center gap-2 min-w-fit">
              <input
                type="checkbox"
                id="filter-money-out"
                className="h-4 w-4"
                checked={!!filters.money_out}
                onChange={(e) => setFilters({ ...filters, money_out: e.target.checked })}
                data-testid="filter-money-out"
              />
              <Label htmlFor="filter-money-out" className="cursor-pointer text-sm whitespace-nowrap text-red-700">
                Argent dehors
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {!loading && orders.length > 0 && (
            <div className="flex items-center justify-end px-4 py-2 border-b bg-slate-50/50">
              <ColumnsToggle
                columns={ORDER_COLUMNS}
                orderedColumns={orderedOrderColumns}
                isVisible={isOrderColVisible}
                toggleVisibility={toggleOrderCol}
                moveColumn={moveOrderColumn}
                resetOrder={resetOrderColumns}
                testId="orders-columns-toggle"
              />
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : orders.length === 0 ? (
            <div className="empty-state">
              <img
                src="https://images.unsplash.com/photo-1760108232787-40ec40f9a706?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHNoYXBlcyUyMG1pbmltYWwlMjBiZWlnZXxlbnwwfHx8fDE3NzI0NTg4MTR8MA&ixlib=rb-4.1.0&q=85"
                alt="Aucune commande"
                className="empty-state-image"
              />
              <h3 className="font-heading text-lg font-medium">Aucune commande</h3>
              <p className="text-muted-foreground mt-1">
                Créez votre première commande pour commencer
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {progressMonthFilter && (
                <div
                  className="mb-2 flex items-center justify-between gap-3 rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm"
                  data-testid="progress-month-filter-banner"
                >
                  <div className="text-indigo-900">
                    <span className="font-medium">Filtre actif :</span> commandes avec un avancement saisi en <strong>{String(progressMonthFilter.month).padStart(2, "0")}/{progressMonthFilter.year}</strong>
                    {progressOrderIds && (
                      <span className="ml-2 text-indigo-700">({progressOrderIds.length} commande{progressOrderIds.length > 1 ? "s" : ""})</span>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setProgressMonthFilter(null); setProgressOrderIds(null); }}
                    data-testid="progress-month-filter-clear"
                  >
                    Retirer le filtre
                  </Button>
                </div>
              )}
              {selectedOrderIds.length > 0 && (
                <div
                  className="mb-2 flex items-center justify-between gap-3 rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm"
                  data-testid="bulk-selection-toolbar"
                >
                  <div className="text-indigo-900 font-medium">
                    {selectedOrderIds.length} commande{selectedOrderIds.length > 1 ? "s" : ""} sélectionnée{selectedOrderIds.length > 1 ? "s" : ""}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setBulkDate(new Date());
                        setBulkDateDialogOpen(true);
                      }}
                      data-testid="bulk-set-date-btn"
                    >
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      Appliquer une date de commande
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setBulkEditOrdersOpen(true)}
                      data-testid="bulk-edit-orders-btn"
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Modifier en masse
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedOrderIds([])}
                      data-testid="bulk-clear-btn"
                    >
                      Désélectionner
                    </Button>
                  </div>
                </div>
              )}
              <OrdersTable
                orders={progressOrderIds ? orders.filter((o) => progressOrderIds.includes(o.id)) : orders}
                logisticsSummary={logisticsSummary}
                filters={filters}
                setFilters={setFilters}
                profitability={profitability}
                orderProgressCumulatives={orderProgressCumulatives}
                timeDeltas={timeDeltas}
                materialMargins={materialMargins}
                orderTypes={orderTypes}
                statusLabels={statusLabels}
                statusColors={statusColors}
                formatCurrency={formatCurrency}
                visibleOrderColumns={visibleOrderColumns}
                isOrderColVisible={isOrderColVisible}
                orderSortArrow={orderSortArrow}
                handleOrderSort={handleOrderSort}
                sortOrders={sortOrders}
                handleToggleDeposit={handleToggleDeposit}
                handleTogglePaid={handleTogglePaid}
                handleMarkCashed={handleMarkCashed}
                openPaymentsDialog={openPaymentsDialog}
                openHoldDialog={openHoldDialog}
                setCompatibleProductsOrder={setCompatibleProductsOrder}
                openDialog={openDialog}
                handleCancelOrder={handleCancelOrder}
                handleToggleHidden={handleToggleHidden}
                handleDelete={handleDelete}
                setWorkSessionsOrder={setWorkSessionsOrder}
                setPvOrder={setPvOrder}
                setReminderDialog={setReminderDialog}
                setProgressDialogOrder={setProgressDialogOrder}
                selectedOrderIds={selectedOrderIds}
                toggleOrderSelection={(id) =>
                  setSelectedOrderIds((curr) =>
                    curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id]
                  )
                }
                toggleSelectAllFiltered={(ids) =>
                  setSelectedOrderIds((curr) =>
                    ids.every((id) => curr.includes(id)) ? [] : ids
                  )
                }
              />
              {hasMore && (
                <div className="flex flex-col items-center justify-center py-6 border-t mt-2 gap-2" data-testid="orders-load-more-container">
                  <p className="text-xs text-muted-foreground">
                    {orders.length} sur {totalCount} commande(s) chargée(s)
                  </p>
                  <div className="flex gap-2 flex-wrap justify-center">
                    <Button
                      variant="outline"
                      onClick={loadMore}
                      disabled={loadingMore}
                      data-testid="orders-load-more-btn"
                    >
                      {loadingMore ? "Chargement..." : `Charger ${Math.min(PAGE_SIZE, totalCount - orders.length)} commandes de plus`}
                    </Button>
                    {totalCount - orders.length > PAGE_SIZE && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          setLoadingMore(true);
                          try {
                            // Charge tout en mode fallback (legacy /api/orders)
                            const apiFilters = {
                              ...(filters.order_date_from && { order_date_from: format(filters.order_date_from, "yyyy-MM-dd") }),
                              ...(filters.order_date_to && { order_date_to: format(filters.order_date_to, "yyyy-MM-dd") }),
                              ...(filters.missing_order_date && { missing_order_date: true }),
                              ...(searchQuery.trim() && { search: searchQuery.trim() }),
                              ...(showHidden && { include_hidden: true }),
                            };
                            const r = await api.getOrders(apiFilters);
                            const all = Array.isArray(r.data) ? r.data : [];
                            setOrders(all);
                            setTotalCount(all.length);
                            setHasMore(false);
                            setAllOrdersCache(all);
                          } catch (_e) {
                            toast.error("Erreur chargement complet");
                          } finally {
                            setLoadingMore(false);
                          }
                        }}
                        disabled={loadingMore}
                        data-testid="orders-load-all-btn"
                      >
                        Tout charger ({totalCount - orders.length} restantes)
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground italic">
                    Astuce : si peu de nouvelles commandes apparaissent après le chargement,
                    pense à vérifier les filtres actifs en haut de page.
                  </p>
                </div>
              )}
              {!hasMore && totalCount > 0 && orders.length === totalCount && totalCount > PAGE_SIZE && (
                <p className="text-xs text-muted-foreground text-center py-3 border-t mt-2">
                  Toutes les commandes sont chargées ({totalCount})
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Dialog */}
      <OrderFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        formData={formData}
        setFormData={setFormData}
        editingOrder={editingOrder}
        profitability={profitability}
        orderTypes={orderTypes}
        sellers={sellers}
        subcontractors={subcontractors}
        onSubmit={handleSubmit}
        onProgressChange={fetchData}
      />


      {/* Bulk Set Order Date Dialog */}
      <BulkOrderDateDialog
        open={bulkDateDialogOpen}
        onOpenChange={setBulkDateDialogOpen}
        selectedCount={selectedOrderIds.length}
        bulkDate={bulkDate}
        setBulkDate={setBulkDate}
        onConfirm={handleBulkSetDate}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteOrderAlertDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={confirmDelete}
      />

      {/* On Hold Dialog */}
      <OnHoldDialog
        open={holdDialogOpen}
        onOpenChange={setHoldDialogOpen}
        orderToHold={orderToHold}
        holdData={holdData}
        setHoldData={setHoldData}
        onConfirm={handleToggleHold}
      />

      {/* Payments Dialog */}
      <PaymentsDialog
        open={paymentsDialogOpen}
        onOpenChange={setPaymentsDialogOpen}
        order={selectedOrderForPayments}
        orderPayments={orderPayments}
        newPayment={newPayment}
        setNewPayment={setNewPayment}
        onAddPayment={handleAddPayment}
        onDeletePayment={handleDeletePayment}
        onRefresh={async () => {
          if (!selectedOrderForPayments?.id) return;
          try {
            const r = await api.getOrderPayments(selectedOrderForPayments.id);
            setOrderPayments(r.data);
          } catch (e) { /* silent */ }
        }}
      />

      <WorkforceWarningDialog
        warning={workforceWarning}
        onClose={() => setWorkforceWarning(null)}
        onConfirm={confirmCreateDespiteWarning}
      />

      <CompatibleProductsDialog
        open={!!compatibleProductsOrder}
        order={compatibleProductsOrder}
        onClose={handleCloseCompatible}
      />

      <PvDialog
        open={!!pvOrder}
        order={pvOrder}
        onClose={() => setPvOrder(null)}
        onSaved={fetchData}
      />

      <WorkSessionsDialog
        open={!!workSessionsOrder}
        order={workSessionsOrder}
        onClose={() => setWorkSessionsOrder(null)}
      />

      {/* Deposit reminder dialog */}
      <SendReminderDialog
        reminderDialog={reminderDialog}
        setReminderDialog={setReminderDialog}
        onSend={handleSendReminder}
      />

      {/* Avancement CA — accès rapide depuis la liste */}
      <OrderProgressDialog
        order={progressDialogOrder}
        onClose={() => setProgressDialogOrder(null)}
        onChange={fetchData}
      />

      {/* Détection doublons */}
      <OrderDuplicatesDialog
        open={duplicatesDialogOpen}
        onClose={() => setDuplicatesDialogOpen(false)}
        onChanged={fetchData}
      />

      {/* Modification en masse */}
      <BulkEditOrdersDialog
        open={bulkEditOrdersOpen}
        onClose={() => setBulkEditOrdersOpen(false)}
        selectedOrderIds={selectedOrderIds}
        sellers={sellers}
        orderTypes={orderTypes}
        onUpdated={() => {
          setSelectedOrderIds([]);
          fetchData();
        }}
      />
      <GlobalLogisticsTodoDialog
        open={globalLogisticsOpen}
        onOpenChange={setGlobalLogisticsOpen}
      />
    </div>
  );
};

export default OrdersPage;
